import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles
} from "@material-ui/core";

import { getHoursMins, remCalc, timeToSeconds } from "../../utils/helpers";
import API from "../../utils/axios";

import Form from "./Form";
import Button from "./Button";

const useStyles = makeStyles(theme => ({
  root: {
    zIndex: "1304 !important"
  },
  paperWidthXs: {
    maxWidth: 480
  },
  dialogTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: theme.typography.fontWeightMedium,
    padding: theme.spacing(3, 4, 0, 4)
  },
  DialogContent: {
    padding: theme.spacing(2, 4)
  },
  selectPopoverClassesRoot: {
    zIndex: "1305 !important"
  },
  dialogContentText: {
    color: "#606165",
    fontSize: theme.typography.pxToRem(14)
  },
  dialogActions: {
    "& button:first-of-type": {
      textTransform: "none",
      boxShadow: "none !important",
      minWidth: remCalc(51),
      padding: theme.spacing(1.25, 2),
      lineHeight: "1rem",
      backgroundColor: "#1329FE !important",
      " &.Mui-disabled,&.Mui-disabled, &.Mui-disabled:hover": {
        backgroundColor: "#1329FE !important",
        color: "#fff",
        opacity: "38%"
      }
    }
  }
}));

const initialFormValues = {
  startLocation: null,
  endLocationOption: "returnToStart",
  endLocation: null,
  startTime: new Date().setHours(8, 0),
  endTime: ""
};

const VehicleSettingsSetup = props => {
  const {
    userData,
    vehicleSettingsSetupStatus,
    setVehicleSettingsSetupStatus
  } = props;

  // General
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // States
  const [showDialog, setShowDialog] = useState(false);
  const [inputValues, setInputValues] = useState(initialFormValues);
  const [errorValues, setErrorValues] = useState({
    startLocation: "",
    endLocationOption: "",
    endLocation: "",
    startTime: "",
    endTime: ""
  });
  const [submittingSettings, setSubmittingSettings] = useState(false);

  // Variables
  const organisation =
    userData.organisationSite && userData.organisationSite.organisation;

  // Callbacks
  const updateInputValues = useCallback((fieldName, fieldValue) => {
    setInputValues(previousInputValues => ({
      ...previousInputValues,
      [fieldName]: fieldValue
    }));
  }, []);

  const handleAddressSelect = useCallback(
    (field, value) => {
      if (value && value.place_id) {
        const service = new window.google.maps.places.PlacesService(
          window.document.createElement("div")
        );
        service.getDetails(
          {
            placeId: value.place_id,
            language: "en",
            fields: ["address_components", "geometry", "formatted_address"]
          },
          function (PlaceResult) {
            updateInputValues(field, PlaceResult);
          }
        );
      } else {
        updateInputValues(field, value);
      }
    },
    [updateInputValues]
  );

  const isValid = (name, defaultVal) => {
    if (!inputValues[name]) {
      updateInputValues(name, defaultVal);
      setErrorValues(previousErrorValues => ({
        ...previousErrorValues,
        previousErrorValues: "",
        [name]: ""
      }));
      return false;
    }
    if (inputValues[name].toString() === "Invalid Date") {
      setErrorValues(previousErrorValues => ({
        ...previousErrorValues,
        [name]: "Invalid time format"
      }));
      return false;
    }
    setErrorValues(previousErrorValues => ({
      ...previousErrorValues,
      [name]: ""
    }));
    return true;
  };

  const timeBetween = (start, end) => {
    if (
      inputValues[start]?.toString() !== "Invalid Date" &&
      inputValues[end]?.toString() !== "Invalid Date"
    )
      return setErrorValues(previousErrorValues => ({
        ...previousErrorValues,
        [start]:
          previousErrorValues[start] === "Invalid time format"
            ? previousErrorValues[start]
            : getHoursMins(inputValues[end]) <= getHoursMins(inputValues[start])
            ? "Invalid time window"
            : ""
      }));
  };

  const handleSubmit = () => {
    const newErrors = {
      ...errorValues,
      startLocation:
        inputValues.startLocation === null
          ? "Please enter a valid start location."
          : "",
      endLocation:
        inputValues.endLocationOption === "otherLocation" &&
        inputValues.endLocation === null
          ? "Please enter a valid end location."
          : ""
    };
    setErrorValues(newErrors);

    if (Object.values(newErrors).filter(Boolean).length === 0) {
      const data = {
        start_address: inputValues.startLocation,
        earliest_start: timeToSeconds(getHoursMins(inputValues.startTime))
      };

      if (inputValues.endLocationOption === "returnToStart") {
        data.end_address = inputValues.startLocation;
      } else if (inputValues.endLocationOption === "otherLocation") {
        data.end_address = inputValues.endLocation;
      }

      if (inputValues.endTime) {
        data.latest_end = timeToSeconds(getHoursMins(inputValues.endTime));
      }
      setSubmittingSettings(true);
      API.post("/v1/settings/vehicles", data)
        .then(() => {
          setVehicleSettingsSetupStatus({ loaded: true, completed: true });
        })
        .catch(error => {
          enqueueSnackbar("Cannot update default vehicle settings.");
        })
        .finally(() => {
          setSubmittingSettings(false);
          setShowDialog(false);
        });
    }
  };

  // Memos
  const firstFormData = useMemo(
    () => [
      [
        {
          disabled: submittingSettings,
          renderInputField: true,
          topLabel: "Start location",
          name: "startLocation",
          placeholder: "Add start location",
          width: 416,
          noMarginTop: true,
          componentType: "vehicleAutoComplete",
          handleAddressSelect: value => {
            handleAddressSelect("startLocation", value);
            if (value) {
              setErrorValues(previousErrors => ({
                ...previousErrors,
                startLocation: ""
              }));
            }
          }
        }
      ],
      [
        {
          disabled: submittingSettings,
          componentType: "select",
          topLabel: "End location",
          name: "endLocationOption",
          noHelperText: true,
          showRadioButton: true,
          classes: {
            PopoverClasses: {
              root: classes.selectPopoverClassesRoot
            }
          },
          options: [
            [
              "returnToStart",
              "Same as start location",
              "Roundtrip (recommended)"
            ],
            [
              "otherLocation",
              "End routes at other location",
              "Add end location"
            ],
            ["dontUse", "Don’t use end location", "Not recommended"]
          ],
          shrink: true,
          showOnlyOptionLabel: true,
          onChange: event => {
            updateInputValues("endLocationOption", event.target.value);
          }
        }
      ]
    ],
    [
      classes.selectPopoverClassesRoot,
      handleAddressSelect,
      submittingSettings,
      updateInputValues
    ]
  );

  const secondFormData = useMemo(
    () => [
      [
        {
          disabled: submittingSettings,
          renderInputField: true,
          label: "Add any end location",
          name: "endLocation",
          width: 416,
          noMarginTop: true,
          componentType: "vehicleAutoComplete",
          handleAddressSelect: val => {
            handleAddressSelect("endLocation", val);
            if (val) {
              setErrorValues(previousErrors => ({
                ...previousErrors,
                endLocation: ""
              }));
            }
          }
        }
      ]
    ],
    [handleAddressSelect, submittingSettings]
  );

  const thirdFormData = [
    [
      {
        disabled: submittingSettings,
        componentType: "timeWindow",
        startTopLabel: "Start time",
        endTopLabel: "End time",
        startName: "startTime",
        endName: "endTime",
        endPlaceholder: "Add end time (optional)",
        onBlurStart: () => {
          isValid("startTime", new Date().setHours(8, 0)) &&
            timeBetween("startTime", "endTime");
        },
        onBlurEnd: () => {
          isValid("endTime", null) && timeBetween("startTime", "endTime");
        },
        onChangeStart: value => {
          updateInputValues("startTime", value);
        },
        onChangeEnd: value => {
          updateInputValues("endTime", value);
        }
      }
    ]
  ];

  useEffect(() => {
    if (!vehicleSettingsSetupStatus.completed) {
      setTimeout(() => setShowDialog(true), 500);
    }
  }, [vehicleSettingsSetupStatus]);

  return (
    <Dialog
      open={showDialog}
      className={classes.root}
      classes={{ paperWidthXs: classes.paperWidthXs }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        Where do your vehicles start their route?
      </DialogTitle>
      <DialogContent className={classes.DialogContent}>
        <DialogContentText className={classes.dialogContentText}>
          Don’t worry about your decision, you can change your preferences at
          any moment.
        </DialogContentText>
        <div style={{ marginTop: 24 }}>
          <Form
            data={firstFormData}
            inputValues={inputValues}
            errorValues={errorValues}
          />
          {inputValues.endLocationOption === "otherLocation" && (
            <Form
              data={secondFormData}
              inputValues={inputValues}
              errorValues={errorValues}
            />
          )}
          <Form
            data={thirdFormData}
            inputValues={inputValues}
            errorValues={errorValues}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          loading={submittingSettings}
          style={{ width: 135, height: 40 }}
        >
          Confirm details
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = state => ({
  userData: state.auth.userData
});

export default connect(mapStateToProps)(VehicleSettingsSetup);
